import React, { FC, Fragment, useEffect, useState } from 'react';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { customerSettings } from 'utils/customerSettings';
import { permissions } from 'utils/permission';
import { CONSTANTS } from 'config/constants';
import { useTranslation } from 'react-i18next';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Tab } from '@mui/material';
import PrivateSpace from 'components/elastic-dashboards/private-space.component';
import LabElasticDashboard from 'components/elastic-dashboards/lab-elastic-dashboard.component';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const { SETTINGS_NAMES } = CONSTANTS;

const Analytics: FC = () => {
    const { t: translate } = useTranslation(['analytics', 'navigation']);
    const navigate = useNavigate();
    const { tab } = useParams<{ tab: string }>();
    const [tabState, setTabState] = useState(tab || 'private-space');
    const currentOrganization = currentOrgVar();
    const currentUser = currentUserVar();
    const isLabUser = permissions.isLabTechnician(currentUser?.type);

    const privateDashboard = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        SETTINGS_NAMES.PRIVATE_ELASTIC_DASHBOARD,
    );
    const isProductionPlanningEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        SETTINGS_NAMES.PRODUCTION_PLANNING,
    );
    const isProductionValueEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        SETTINGS_NAMES.PRODUCTION_VALUE_REPORT,
    );

    const handleTabChange = (_: unknown, newTabState: string) => {
        if (tabState !== newTabState) {
            navigate(`/analytics/${newTabState}`);
            setTabState(newTabState);
        }
    };

    useEffect(() => {
        if (privateDashboard) {
            setTabState('private-space');
        }
    }, []);

    // Clinic users only have the private space — the standard dashboards live in the lab Kibana space
    if (!isLabUser && !privateDashboard) {
        return null;
    }

    return (
        <Fragment>
            <Helmet>
                <title>{translate('navigation:ANALYTICS')} | Denthub</title>
            </Helmet>
            <TabContext value={tabState}>
                <TabList onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                    {privateDashboard ? (
                        <Tab label={translate('analytics:PRIVATE_SPACE')} value="private-space" />
                    ) : undefined}

                    {isLabUser
                        ? [
                              <Tab
                                  label={translate('analytics:ORDER_OVERVIEW')}
                                  value="order-overview"
                                  key="order-overview"
                              />,
                              <Tab
                                  label={translate('analytics:PRODUCTION_OVERVIEW')}
                                  value="production-overview"
                                  key="production-overview"
                              />,
                              <Tab
                                  label={translate('analytics:WARRANTY_OVERVIEW')}
                                  value="warranty-overview"
                                  key="warranty-overview"
                              />,
                              <Tab
                                  label={translate('analytics:ACTIONS_MATERIALS')}
                                  value="actions-materials"
                                  key="actions-materials"
                              />,
                              <Tab label={translate('analytics:INVOICING')} value="invoicing" key="invoicing" />,
                          ]
                        : undefined}
                    {isLabUser && isProductionPlanningEnabled ? (
                        <Tab label={translate('analytics:WORKFLOW_PLANNING')} value="workflow-planning" />
                    ) : undefined}
                    {isLabUser && isProductionValueEnabled ? (
                        <Tab label={translate('analytics:TECHNICIAN_STATS')} value="technician-stats" />
                    ) : undefined}
                </TabList>

                {privateDashboard ? (
                    <TabPanel sx={{ padding: 0, marginTop: 0.5 }} value="private-space">
                        <PrivateSpace dashboard={privateDashboard} />
                    </TabPanel>
                ) : undefined}
                {isLabUser ? (
                    <Fragment>
                        <TabPanel sx={{ padding: 0, marginTop: 0.5 }} value="order-overview">
                            <LabElasticDashboard dashboardFramePath="/view/7174c7d0-4e12-11ed-95aa-fdc4a42c03e0?embed=true&_g=(filters%3A!()%2CrefreshInterval%3A(pause%3A!t%2Cvalue%3A0)%2Ctime%3A(from%3Anow-30d%2Fd%2Cto%3Anow))&show-time-filter=true" />
                        </TabPanel>
                        <TabPanel sx={{ padding: 0, marginTop: 0.5 }} value="production-overview">
                            <LabElasticDashboard dashboardFramePath="/view/ae936590-b1d1-11ed-8e13-2794461b2f23?embed=true&_g=(filters%3A!()%2CrefreshInterval%3A(pause%3A!t%2Cvalue%3A0)%2Ctime%3A(from%3Anow-30d%2Fd%2Cto%3Anow))&show-time-filter=true" />
                        </TabPanel>
                        <TabPanel sx={{ padding: 0, marginTop: 0.5 }} value="warranty-overview">
                            <LabElasticDashboard dashboardFramePath="/view/1e3520d0-b684-11ed-8e13-2794461b2f23?embed=true&_g=(filters%3A!()%2CrefreshInterval%3A(pause%3A!t%2Cvalue%3A0)%2Ctime%3A(from%3Anow-30d%2Fd%2Cto%3Anow))&show-time-filter=true" />
                        </TabPanel>
                        <TabPanel sx={{ padding: 0, marginTop: 0.5 }} value="actions-materials">
                            <LabElasticDashboard dashboardFramePath="/view/8b7f85d1-0427-47bc-885a-0e1da6b60bdf?embed=true&_g=(filters%3A!()%2CrefreshInterval%3A(pause%3A!t%2Cvalue%3A0)%2Ctime%3A(from%3Anow-30d%2Fd%2Cto%3Anow))&show-time-filter=true" />
                        </TabPanel>
                        <TabPanel sx={{ padding: 0, marginTop: 0.5 }} value="invoicing">
                            <LabElasticDashboard dashboardFramePath="/view/915b7d56-2fff-4824-9b7b-480afe307030?embed=true&_g=(filters%3A!()%2CrefreshInterval%3A(pause%3A!t%2Cvalue%3A0)%2Ctime%3A(from%3Anow-30d%2Fd%2Cto%3Anow))&show-time-filter=true" />
                        </TabPanel>
                        <TabPanel sx={{ padding: 0, marginTop: 0.5 }} value="workflow-planning">
                            <LabElasticDashboard dashboardFramePath="/view/539dd16e-42da-43b3-834d-22bc5c8ad0c1?embed=true&_g=(filters%3A!()%2CrefreshInterval%3A(pause%3A!t%2Cvalue%3A0)%2Ctime%3A(from%3Anow-30d%2Fd%2Cto%3Anow))&show-time-filter=true" />
                        </TabPanel>
                        <TabPanel sx={{ padding: 0, marginTop: 0.5 }} value="technician-stats">
                            <LabElasticDashboard dashboardFramePath="/view/101d6500-b1b5-4dd3-b94c-d4bd4212bd8b?embed=true&_g=(filters%3A!()%2CrefreshInterval%3A(pause%3A!t%2Cvalue%3A0)%2Ctime%3A(from%3Anow-30d%2Fd%2Cto%3Anow))&show-time-filter=true" />
                        </TabPanel>
                    </Fragment>
                ) : undefined}
            </TabContext>
        </Fragment>
    );
};

export default Analytics;
